import { IconRefresh } from '@tabler/icons-react';

import React, { useCallback } from 'react';

import { Alert, Button, Group, Paper, Stack, Title } from '@mantine/core';

import SimpleMermaidEditor from './SimpleMermaidEditor';

interface MermaidDiagramBoxProps {
    title: string;
    icon: React.ReactNode;
    chart: string;
    onChange: (newChart: string) => void;
    onDelete: () => void;
    onRegenerate?: () => void;
    regenerateLabel?: string;
    isGenerating?: boolean;
    error?: string | null;
    showEmptyMessage?: boolean;
    emptyMessage?: string;
    emptyAction?: React.ReactNode;
    onAiGenerate?: (mermaidCode: string) => void;
    considerations?: React.ReactNode;
}

/**
 * Shared Mermaid diagram component with auto-save indicator.
 * Used by both Timeline and generic Mermaid components.
 */
export const MermaidDiagramBox: React.FC<MermaidDiagramBoxProps> = ({
    title,
    icon,
    chart,
    onChange,
    onDelete,
    onRegenerate,
    regenerateLabel = 'Regenerate',
    isGenerating = false,
    error,
    showEmptyMessage = true,
    emptyMessage = 'No diagram to display',
    emptyAction,
    onAiGenerate,
    considerations
}) => {
    // Handle delete with confirmation
    const handleDelete = useCallback(() => {
        if (window.confirm(`Delete this ${title.toLowerCase()}?`)) {
            onDelete();
        }
    }, [title, onDelete]);

    return (
        <Paper shadow='sm' p='md' radius='md' withBorder>
            <Stack gap='md'>
                <Group justify='space-between'>
                    <Group>
                        {icon}
                        <Title order={4}>{title}</Title>
                    </Group>
                    <Group>
                        {onRegenerate && (
                            <Button
                                leftSection={<IconRefresh size={16} />}
                                onClick={onRegenerate}
                                loading={isGenerating}
                                variant='light'
                            >
                                {regenerateLabel}
                            </Button>
                        )}
                        <Button
                            onClick={handleDelete}
                            color='red'
                            variant='subtle'
                            size='xs'
                        >
                            Delete
                        </Button>
                    </Group>
                </Group>

                {considerations && considerations}

                {error && (
                    <Alert color='red' title='Error'>
                        {error}
                    </Alert>
                )}

                {!chart && !isGenerating && showEmptyMessage && (
                    <Alert color='blue' title='No Diagram'>
                        <Stack gap='sm'>
                            <div>{emptyMessage}</div>
                            {emptyAction}
                        </Stack>
                    </Alert>
                )}

                {chart && (
                    <div style={{ position: 'relative' }}>
                        <SimpleMermaidEditor
                            value={chart}
                            onChange={onChange}
                            height={400}
                            onAiGenerate={onAiGenerate}
                        />
                    </div>
                )}
            </Stack>
        </Paper>
    );
};
